import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { api, formatApiError } from "../lib/api";
import { BRAND, COLORS } from "../lib/brand";
import { Check, Truck, Package, FileSearch, Globe2, MapPin, ExternalLink, Info } from "lucide-react";

const STEPS = [
  { key: "ready_for_processing", label: "Ready for Processing", icon: FileSearch },
  { key: "in_processing", label: "In Processing", icon: Package },
  { key: "embassy_legalization", label: "Embassy Legalization", icon: Globe2, conditional: true },
  { key: "complete", label: "Complete", icon: Check },
  { key: "shipped", label: "Shipped / Ready for Pickup", icon: Truck },
];

const SERVICE_LABEL = {
  standard: "Standard State Apostille",
  expedited: "Expedited State Apostille",
  federal: "Federal Processing",
  embassy: "Embassy Legalization",
};

const DELIVERY_LABEL = {
  front_door: "Front Door / Courier Delivery",
  in_office_pickup: "In-Office Pickup",
  domestic_ship: "Domestic Shipping",
  intl_ship: "International Shipping",
};

export default function TrackOrderPage() {
  const { token } = useParams();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    api
      .get(`/apostille/track/${token}`)
      .then((r) => setOrder(r.data))
      .catch((e) => setError(formatApiError(e)))
      .finally(() => setLoading(false));
  }, [token]);

  if (loading) {
    return <div className="min-h-screen flex items-center justify-center text-[#5C5C5C]">Loading your order...</div>;
  }

  if (error || !order) {
    return (
      <div className="min-h-screen flex items-center justify-center px-4">
        <div className="bg-white border border-[#E8E2D5] rounded-xl p-8 max-w-md text-center">
          <h1 className="text-xl font-semibold text-[#1A1A1A]">We couldn't find that order</h1>
          <p className="text-sm text-[#5C5C5C] mt-2">{error || "The tracking link is invalid or expired."}</p>
        </div>
      </div>
    );
  }

  const visibleSteps = STEPS.filter((s) => !s.conditional || order.needs_embassy);
  const currentIdx = visibleSteps.findIndex((s) => s.key === order.status);

  return (
    <div className="min-h-screen bg-[#FBF8F1]" data-testid="track-order-page">
      {/* Header bar */}
      <div className="text-white" style={{ background: `linear-gradient(135deg, ${COLORS.maroonDeep} 0%, ${COLORS.maroon} 100%)` }}>
        <div className="max-w-3xl mx-auto px-4 py-8 flex items-center gap-4">
          <img src={BRAND.logoUrl} alt="" className="w-14 h-14 rounded-full border-2 border-[#D4AF37]" />
          <div className="flex-1">
            <div className="text-xs uppercase tracking-wider opacity-80">{BRAND.name}</div>
            <h1 className="text-2xl font-semibold">Hi {order.first_name},</h1>
            <p className="text-sm opacity-80">Here's the live status of your apostille order.</p>
          </div>
        </div>

        <div className="max-w-3xl mx-auto px-4 pb-6">
          <div className="bg-white/10 backdrop-blur-sm rounded-lg p-4 flex items-start gap-3">
            <div className="text-[#D4AF37] shrink-0 mt-0.5"><Check size={20} /></div>
            <div className="flex-1">
              <div className="text-xs uppercase tracking-wider opacity-80">Estimated Ready Date</div>
              <div className="text-xl font-medium">{order.estimated_ready_date ? new Date(order.estimated_ready_date + "T12:00:00").toLocaleDateString(undefined, { weekday: "long", month: "long", day: "numeric" }) : "Pending review"}</div>
              <p className="text-[11px] opacity-70 mt-1">Estimates are ~99% accurate but may shift due to government processing volume, embassy schedules, or weather delays.</p>
            </div>
          </div>
        </div>
      </div>

      {/* Progress */}
      <div className="max-w-3xl mx-auto px-4 py-10">
        <section className="bg-white rounded-2xl border border-[#E8E2D5] shadow-sm p-6 md:p-8">
          <h2 className="text-lg font-medium text-[#1A1A1A] mb-6">Order Progress</h2>

          <ol className="space-y-5">
            {visibleSteps.map((s, idx) => {
              const Icon = s.icon;
              const done = idx < currentIdx;
              const active = idx === currentIdx;
              return (
                <li key={s.key} className="flex items-start gap-4" data-testid={`milestone-${s.key}`}>
                  <div className={`shrink-0 w-10 h-10 rounded-full flex items-center justify-center border-2 ${
                    done ? "bg-[#800000] border-[#800000] text-white"
                      : active ? "bg-white border-[#D4AF37] text-[#800000]"
                      : "bg-white border-[#E8E2D5] text-[#5C5C5C]"
                  }`}>
                    <Icon size={18} />
                  </div>
                  <div className="flex-1 pb-3">
                    <div className={`font-medium ${active ? "text-[#800000]" : "text-[#1A1A1A]"}`}>{s.label}</div>
                    {active && <div className="text-xs text-[#D4AF37] uppercase tracking-wider mt-0.5">Current status</div>}
                    {done && <div className="text-xs text-[#5C5C5C] mt-0.5">Completed</div>}
                  </div>
                </li>
              );
            })}
          </ol>

          {/* progress bar */}
          <div className="mt-6 h-2 w-full rounded-full bg-[#FBF8F1] overflow-hidden">
            <div
              className="h-full transition-all duration-700"
              style={{
                width: `${Math.max(8, ((currentIdx + 1) / visibleSteps.length) * 100)}%`,
                background: `linear-gradient(90deg, ${COLORS.maroon}, ${COLORS.gold})`,
              }}
            />
          </div>
        </section>

        {/* Conditional final-state block */}
        {order.status === "complete" && order.delivery_method === "in_office_pickup" && order.office_address && (
          <section className="mt-6 bg-white rounded-2xl border border-[#E8E2D5] shadow-sm p-6">
            <div className="flex items-start gap-3">
              <MapPin className="text-[#800000] shrink-0 mt-0.5" size={20} />
              <div className="flex-1">
                <h3 className="font-medium text-[#1A1A1A]">Ready for In-Office Pickup</h3>
                <p className="text-sm text-[#5C5C5C] mt-1">{order.office_address}</p>
                <a
                  href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(order.office_address)}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1.5 mt-3 px-4 py-2 rounded-md text-sm font-medium text-white"
                  style={{ background: COLORS.maroon }}
                  data-testid="get-directions-btn"
                >
                  Get Directions <ExternalLink size={14} />
                </a>
              </div>
            </div>
          </section>
        )}

        {order.status === "shipped" && order.tracking_link && (
          <section className="mt-6 bg-white rounded-2xl border border-[#E8E2D5] shadow-sm p-6">
            <div className="flex items-start gap-3">
              <Truck className="text-[#800000] shrink-0 mt-0.5" size={20} />
              <div className="flex-1">
                <h3 className="font-medium text-[#1A1A1A]">Your package is in transit</h3>
                <p className="text-sm text-[#5C5C5C] mt-1">Track it live with the carrier.</p>
                <a
                  href={order.tracking_link}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1.5 mt-3 px-4 py-2 rounded-md text-sm font-medium text-white"
                  style={{ background: COLORS.maroon }}
                  data-testid="tracking-link-btn"
                >
                  Open Tracking <ExternalLink size={14} />
                </a>
              </div>
            </div>
          </section>
        )}

        {/* Order summary */}
        <section className="mt-6 bg-white rounded-2xl border border-[#E8E2D5] shadow-sm p-6">
          <h3 className="font-medium text-[#1A1A1A] mb-3">Order Summary</h3>
          <dl className="text-sm divide-y divide-[#E8E2D5]">
            <Row label="Service Level" value={SERVICE_LABEL[order.service_level]} />
            <Row label="Destination" value={order.destination_country} />
            <Row label="Document Count" value={order.document_count} />
            <Row label="Delivery Method" value={DELIVERY_LABEL[order.delivery_method]} />
            <Row label="Submitted" value={new Date(order.created_at).toLocaleDateString()} />
          </dl>
        </section>

        <div className="mt-6 flex items-start gap-2 text-xs text-[#5C5C5C]">
          <Info size={14} className="mt-0.5" />
          <p>Bookmark this page to check progress anytime. Questions? Email <a className="text-[#800000]" href={`mailto:${BRAND.email}`}>{BRAND.email}</a> or call <a className="text-[#800000]" href={`tel:${BRAND.phoneRaw}`}>{BRAND.phone}</a>.</p>
        </div>
      </div>
    </div>
  );
}

function Row({ label, value }) {
  return (
    <div className="flex items-center justify-between py-2.5">
      <span className="text-[#5C5C5C]">{label}</span>
      <span className="text-[#1A1A1A] font-medium text-right">{value}</span>
    </div>
  );
}
